import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Chip,
  withStyles,
  Box,
  Divider,
  Link,
} from '@material-ui/core';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import GetAppIcon from '@material-ui/icons/GetApp';

const styles = theme => ({
  dialogContent: {
    padding: theme.spacing(3),
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 0),
  },
  label: {
    color: theme.palette.text.secondary,
  },
  value: {},
  commentHeader: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
  },
  commentBody: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  attachmentsSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  attachmentLink: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderRadius: theme.shape.borderRadius,
    },
  },
  attachmentIcon: {
    marginRight: theme.spacing(1),
  },
  attachmentInfo: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  attachmentSize: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
});

const DetailRow = ({ label, children, classes }) => (
  <div className={classes.detailRow}>
    <Typography variant="body1" className={classes.label}>{label}</Typography>
    <Typography variant="body1" className={classes.value}>{children}</Typography>
  </div>
);

const QuickNoteDetailModal = ({ open, onClose, note, classes }) => {
  if (!note) {
    return null;
  }

  // Helper function to clean category names for display
  const cleanCategoryName = (categoryName) => {
    if (!categoryName) return '';
    const cleaned = categoryName
      .replace('Practice Exchange   ', '')
      .replace('Succession Planning   ', '');
    
    // Capitalize 3-letter words (like rsp, erd, etc.)
    return cleaned.split(' ').map(word => 
      word.length === 3 ? word.toUpperCase() : word
    ).join(' ');
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent className={classes.dialogContent}>
        <Button onClick={onClose} className={classes.closeButton}>
          CLOSE
        </Button>

        <Box mt={4}>
          <DetailRow label="Date Created:" classes={classes}>
            {new Date(note.created_at).toLocaleString()}
          </DetailRow>
          <Divider />
          <DetailRow label="Interaction Date:" classes={classes}>
            {new Date(note.interaction_date).toLocaleDateString()}
          </DetailRow>
          <Divider />
          <DetailRow label="User:" classes={classes}>
            {note.user?.allowed_name || note.user?.name}
          </DetailRow>
          <Divider />
          <DetailRow label="Inventory User:" classes={classes}>
            {note.commentable?.name}
          </DetailRow>
          <Divider />
          <DetailRow label="Branch:" classes={classes}>
            {note.commentable?.branch || 'N/A'}
          </DetailRow>
          <Divider />
          <DetailRow label="Linked Case/Deal:" classes={classes}>
            {note.pipeline_card ? (
              <Box textAlign="right">
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {note.pipeline_card.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {note.pipeline_card.pipeline?.name}
                </Typography>
              </Box>
            ) : (
              'N/A'
            )}
          </DetailRow>
          <Divider />
          <DetailRow label="Categories:" classes={classes}>
            {note.all_categories && note.all_categories.length > 0 ? (
              <Box display="flex" flexWrap="wrap">
                {note.all_categories.map((category) => (
                  <Chip 
                    key={category.id}
                    label={cleanCategoryName(category.name)} 
                    size="small" 
                    className={classes.chip} 
                  />
                ))}
              </Box>
            ) : note.quickNoteCategory?.name ? (
              <Chip label={cleanCategoryName(note.quickNoteCategory.name)} size="small" className={classes.chip} />
            ) : 'N/A'}
          </DetailRow>
          <Divider />
          <DetailRow label="Activity Type:" classes={classes}>
             {note.activityType?.name ? (
              <Chip label={note.activityType.name} size="small" variant="outlined" className={classes.chip} />
            ) : 'N/A'}
          </DetailRow>
          <Divider />
          <DetailRow label="Time Spent:" classes={classes}>
            {note.time_spent_formatted}
          </DetailRow>
          <Divider />
          <DetailRow label="Type:" classes={classes}>
            <Chip 
              label={note.planned_status_text || (note.planned ? 'Planned' : 'Unplanned')} 
              size="small" 
              variant={note.planned ? 'default' : 'outlined'}
              color={note.planned ? 'primary' : 'default'}
              className={classes.chip}
            />
          </DetailRow>
        </Box>
        
        <Typography variant="h5" className={classes.commentHeader}>
          Notes
        </Typography>
        <Typography variant="body1" className={classes.commentBody}>
          {note.comment}
        </Typography>

        {note.documents && note.documents.length > 0 && (
          <Box className={classes.attachmentsSection}>
            <Typography variant="h6" gutterBottom style={{ display: 'flex', alignItems: 'center' }}>
              <AttachFileIcon style={{ marginRight: 8 }} />
              Attachments ({note.documents.length})
            </Typography>
            {note.documents.map((doc) => (
              <Link
                key={doc.id}
                href={doc.download_url}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.attachmentLink}
              >
                <GetAppIcon color="primary" className={classes.attachmentIcon} />
                <Box className={classes.attachmentInfo}>
                  <Typography variant="body2">
                    {doc.name || 'Unnamed File'}
                  </Typography>
                  <Typography variant="caption" className={classes.attachmentSize}>
                    {formatFileSize(doc.file_size)} {doc.mime_type ? `• ${doc.mime_type}` : ''}
                  </Typography>
                </Box>
              </Link>
            ))}
          </Box>
        )}
        
      </DialogContent>
    </Dialog>
  );
};

QuickNoteDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  note: PropTypes.object,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(QuickNoteDetailModal); 